/**
 * Utilitário para tratamento de erros nas operações de proposta.
 * Transforma mensagens técnicas do backend em mensagens amigáveis para o usuário final.
 */

/**
 * Simplifica mensagens de validação do backend (erro 400)
 */
function simplifyValidationMessage(message) {
  const validationPatterns = [
    {
      pattern: /campo.*obrigat[oó]rio/i,
      replacement: 'Por favor, preencha todos os campos obrigatórios',
    },
    {
      pattern: /formato.*inv[aá]lido/i,
      replacement: 'Verifique o formato dos dados informados',
    },
    {
      pattern: /data.*inv[aá]lida/i,
      replacement: 'Verifique as datas informadas',
    },
    {
      pattern: /valor.*inv[aá]lido/i,
      replacement: 'Verifique os valores informados',
    },
  ]

  // eslint-disable-next-line no-restricted-syntax
  for (let i = 0; i < validationPatterns.length; i += 1) {
    const item = validationPatterns[i]
    if (item.pattern.test(message)) {
      return item.replacement
    }
  }

  if (message.length > 100) {
    return 'Por favor, verifique os dados informados e tente novamente'
  }

  return message
}

/**
 * Simplifica mensagens de regra de negócio do backend (erro 409)
 */
function simplifyBusinessMessage(message) {
  const businessPatterns = [
    {
      pattern: /template.*email/i,
      replacement: 'Template de email não configurado. Entre em contato com o suporte',
    },
    {
      pattern: /proposta.*inv[aá]lida/i,
      replacement: 'Não foi possível processar a proposta. Entre em contato com o suporte',
    },
    {
      pattern: /servi[çc]os.*eventuais/i,
      replacement: message,
    },
  ]

  // eslint-disable-next-line no-restricted-syntax
  for (let i = 0; i < businessPatterns.length; i += 1) {
    const item = businessPatterns[i]
    if (item.pattern.test(message)) {
      return item.replacement
    }
  }

  if (message.length <= 150) {
    return message
  }

  return 'Não foi possível completar a operação devido a uma regra de negócio'
}

/**
 * Categoriza erros HTTP e retorna mensagens amigáveis para o usuário.
 *
 * Fluxo de tratamento:
 * 1. Loga erro completo no console para debug
 * 2. Extrai mensagem do backend
 * 3. Categoriza por status code HTTP
 * 4. Retorna { message, type, originalMessage }
 *
 * @param {Error} error - Objeto de erro do axios
 * @returns {Object} { message: string, type: string, originalMessage: string }
 */
export function handlePropostaError(error) {
  const backendMessage = error?.response?.data?.detail || error?.message || 'Erro desconhecido'
  const statusCode = error?.response?.status

  switch (statusCode) {
    case 400: {
      let validationMessage = simplifyValidationMessage(backendMessage)

      if (/cnpj/i.test(backendMessage)) {
        validationMessage = 'CNPJ inválido ou incorreto'
      } else if (/cpf/i.test(backendMessage)) {
        validationMessage = 'CPF inválido ou incorreto'
      } else if (/email/i.test(backendMessage)) {
        validationMessage = 'Email inválido ou incorreto'
      } else if (/telefone|celular/i.test(backendMessage)) {
        validationMessage = 'Telefone/celular inválido ou incorreto'
      } else if (/data/i.test(backendMessage)) {
        validationMessage = 'Data inválida ou incorreta'
      }

      return {
        message: validationMessage,
        type: 'validation',
        originalMessage: backendMessage,
      }
    }

    case 401:
      return {
        message: 'Sessão expirada. Faça login novamente',
        type: 'authentication',
        originalMessage: backendMessage,
      }

    case 403:
      return {
        message: 'Você não possui permissão para realizar esta ação',
        type: 'permission',
        originalMessage: backendMessage,
      }

    case 404:
      return {
        message: 'Proposta não encontrada',
        type: 'not_found',
        originalMessage: backendMessage,
      }

    case 409:
      return {
        message: simplifyBusinessMessage(backendMessage),
        type: 'business',
        originalMessage: backendMessage,
      }

    case 422:
      return {
        message: 'Verifique os dados informados e tente novamente',
        type: 'validation',
        originalMessage: backendMessage,
      }

    case 500:
    case 502:
    case 503:
    case 504: {
      const serverMessage = backendMessage && backendMessage !== 'Erro desconhecido' && backendMessage.length < 200
        ? backendMessage
        : 'Erro no servidor. Tente novamente em alguns instantes'

      return {
        message: serverMessage,
        type: 'server',
        originalMessage: backendMessage,
      }
    }

    default: {
      if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') {
        return {
          message: 'Tempo de conexão esgotado. Verifique sua internet e tente novamente',
          type: 'timeout',
          originalMessage: backendMessage,
        }
      }

      return {
        message: 'Não foi possível completar a operação. Tente novamente',
        type: 'generic',
        originalMessage: backendMessage,
      }
    }
  }
}

/**
 * Valida campos obrigatórios da proposta antes de enviar ao backend.
 *
 * Regras de validação:
 * - Pessoa: sempre obrigatória
 * - Gerente/Responsável: sempre obrigatório
 * - Empresa/Contratante: obrigatório EXCETO quando:
 *   • proposta.semContratante = true (checkbox "Sem Empresa Cadastrada")
 *   • proposta.osConstituicao = true (checkbox "Ordem de Serviço de Constituição")
 *   • proposta.tipoProposta === 'constituicao' (Proposta de constituição)
 *   • proposta.tipoProposta === 'transferencia' (Proposta de transferência)
 * - Tipo de Proposta: sempre obrigatório
 *
 * @param {Object} proposta - Objeto da proposta
 * @returns {Object} { valid: boolean, message: string, field: string }
 */
export function validatePropostaObrigatorios(proposta) {
  if (!proposta.pessoa || !proposta.pessoa.id) {
    return {
      valid: false,
      message: 'Selecione uma pessoa antes de salvar a proposta',
      field: 'pessoa',
    }
  }

  if (!proposta.gerente || !proposta.gerente.id) {
    return {
      valid: false,
      message: 'Selecione um responsável antes de salvar a proposta',
      field: 'gerente',
    }
  }

  const exemptFromContratante = proposta.semContratante
                              || proposta.osConstituicao
                              || proposta.tipoProposta === 'constituicao'
                              || proposta.tipoProposta === 'transferencia'

  if (!exemptFromContratante && (!proposta.empresasPessoa || proposta.empresasPessoa.length === 0)) {
    return {
      valid: false,
      message: 'Selecione ao menos um contratante antes de salvar a proposta',
      field: 'empresasPessoa',
    }
  }

  if (!proposta.tipoProposta) {
    return {
      valid: false,
      message: 'Selecione o tipo de proposta antes de salvar',
      field: 'tipoProposta',
    }
  }

  if (proposta.tipoProposta === 'semchecklist') {
    const hasServicoPrincipal = Array.isArray(proposta.servicos)
      && proposta.servicos.some(item => item?.servicoId || item?.servico?.id)
    const hasServicoAdicional = Array.isArray(proposta.servicosAdicionais)
      && proposta.servicosAdicionais.some(item => item?.servicoId || item?.servico?.id)
    const hasPacote = Array.isArray(proposta.propostasPacotes)
      && proposta.propostasPacotes.some(pacote => (
        pacote
        && (
          (Array.isArray(pacote.servicos) && pacote.servicos.length > 0)
          || pacote.id
          || pacote.pacoteServicoPropostaId
        )
      ))

    if (!hasPacote && !hasServicoPrincipal && !hasServicoAdicional) {
      return {
        valid: false,
        message: 'Selecione ao menos um serviço antes de salvar a proposta',
        field: 'servicos',
      }
    }
  }

  return {
    valid: true,
    message: '',
    field: null,
  }
}

/**
 * Cria objeto estruturado para logging de erros.
 * Útil para enviar logs para sistemas de monitoramento.
 */
export function createErrorLog(operation, error, context = {}) {
  return {
    operation,
    timestamp: new Date().toISOString(),
    error: {
      message: error?.message,
      status: error?.response?.status,
      data: error?.response?.data,
    },
    context,
    userAgent: navigator.userAgent,
  }
}
